import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { 
    BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, 
    Tooltip, Legend, ResponsiveContainer, AreaChart, Area
} from 'recharts';
import { 
    TrendingUp, TrendingDown, Users, Eye, MousePointer, Heart,
    MessageCircle, Share2, Bookmark, Play, ChevronLeft, ChevronRight,
    Plus, Edit, Trash2, Instagram, Facebook, Youtube, Linkedin, Twitter, Globe,
    BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Channel configuration
const channels = [
    { id: 'instagram', name: 'Instagram', icon: Instagram, color: '#E4405F' },
    { id: 'facebook', name: 'Facebook', icon: Facebook, color: '#1877F2' },
    { id: 'youtube', name: 'YouTube', icon: Youtube, color: '#FF0000' },
    { id: 'linkedin', name: 'LinkedIn', icon: Linkedin, color: '#0A66C2' },
    { id: 'twitter', name: 'Twitter/X', icon: Twitter, color: '#1DA1F2' },
    { id: 'tiktok', name: 'TikTok', icon: Globe, color: '#000000' }
];

const KPICard = ({ title, value, change, icon: Icon, color = 'primary' }) => {
    const isPositive = change >= 0;
    
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-border/50 dark:border-gray-700"
        >
            <div className="flex items-start justify-between">
                <div>
                    <p className="text-sm text-muted-foreground">{title}</p>
                    <p className="text-2xl font-bold mt-1 dark:text-white">
                        {typeof value === 'number' ? value.toLocaleString('de-DE') : value}
                    </p>
                </div>
                <div className={`w-10 h-10 rounded-lg bg-${color}/10 flex items-center justify-center`}>
                    <Icon className={`w-5 h-5 text-${color}`} />
                </div>
            </div>
            {change !== undefined && (
                <div className={`flex items-center gap-1 mt-2 text-sm ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                    {isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                    <span>{isPositive ? '+' : ''}{change}%</span>
                    <span className="text-muted-foreground ml-1">vs. Vormonat</span>
                </div>
            )}
        </motion.div>
    );
};

const ReportsSection = ({ customerId, isPublic = false, password = '' }) => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMonth, setSelectedMonth] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    const [selectedChannel, setSelectedChannel] = useState('all');

    const fetchReports = useCallback(async () => {
        try {
            const endpoint = isPublic 
                ? `${API_URL}/api/public/customer/${customerId}/reports`
                : `${API_URL}/api/customers/${customerId}/reports`;
            
            const params = new URLSearchParams();
            if (password) params.append('password', password);
            
            const response = await axios.get(`${endpoint}?${params.toString()}`, {
                headers: !isPublic ? { Authorization: `Bearer ${localStorage.getItem('token')}` } : {}
            });
            setReports(response.data);
        } catch (error) {
            console.error('Error fetching reports:', error);
        } finally {
            setLoading(false);
        }
    }, [customerId, isPublic, password]);

    useEffect(() => {
        fetchReports();
    }, [fetchReports]);

    // Filter reports by selected month
    const filteredReports = reports.filter(r => {
        const matchesMonth = !selectedMonth || r.month === selectedMonth;
        const matchesChannel = selectedChannel === 'all' || r.channel === selectedChannel;
        return matchesMonth && matchesChannel;
    });

    // Aggregate KPIs
    const totalKPIs = filteredReports.reduce((acc, r) => ({
        reach: acc.reach + (r.reach || 0),
        impressions: acc.impressions + (r.impressions || 0),
        engagement: acc.engagement + (r.engagement || 0),
        clicks: acc.clicks + (r.clicks || 0),
        followers_gained: acc.followers_gained + (r.followers_gained || 0),
        likes: acc.likes + (r.likes || 0),
        comments: acc.comments + (r.comments || 0),
        shares: acc.shares + (r.shares || 0)
    }), { reach: 0, impressions: 0, engagement: 0, clicks: 0, followers_gained: 0, likes: 0, comments: 0, shares: 0 });

    // Chart data
    const chartData = channels.map(channel => {
        const channelReport = filteredReports.find(r => r.channel === channel.id) || {};
        return {
            name: channel.name,
            reach: channelReport.reach || 0,
            impressions: channelReport.impressions || 0,
            engagement: channelReport.engagement || 0,
            clicks: channelReport.clicks || 0,
            color: channel.color
        };
    }).filter(d => d.reach > 0 || d.impressions > 0);

    // Get available months from reports
    const availableMonths = [...new Set(reports.map(r => r.month))].sort().reverse();

    const navigateMonth = (direction) => {
        const [year, month] = selectedMonth.split('-').map(Number);
        const newDate = new Date(year, month - 1 + direction, 1);
        setSelectedMonth(`${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}`);
    };

    const formatMonth = (monthStr) => {
        if (!monthStr) return '';
        const [year, month] = monthStr.split('-');
        const date = new Date(year, month - 1);
        return date.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (reports.length === 0) {
        return (
            <div className="text-center py-12">
                <BarChart3 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-muted-foreground">Keine Reports vorhanden</h3>
                <p className="text-sm text-muted-foreground mt-1">Reports werden hier angezeigt, sobald Daten verfügbar sind.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
                        <BarChart3 className="w-5 h-5 text-white" />
                    </div>
                    <h2 className="text-2xl font-semibold dark:text-white">Performance Reports</h2>
                </div>
                
                <div className="flex items-center gap-3">
                    {/* Month Navigation */}
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => navigateMonth(-1)}>
                            <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <span className="text-sm font-medium px-3 min-w-[140px] text-center dark:text-white">
                            {formatMonth(selectedMonth)}
                        </span>
                        <Button variant="outline" size="sm" onClick={() => navigateMonth(1)}>
                            <ChevronRight className="w-4 h-4" />
                        </Button>
                    </div>

                    {/* Channel Filter */}
                    <Select value={selectedChannel} onValueChange={setSelectedChannel}>
                        <SelectTrigger className="w-[150px]">
                            <SelectValue placeholder="Alle Kanäle" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Alle Kanäle</SelectItem>
                            {channels.map(channel => (
                                <SelectItem key={channel.id} value={channel.id}>
                                    <div className="flex items-center gap-2">
                                        <channel.icon className="w-4 h-4" />
                                        {channel.name}
                                    </div>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KPICard 
                    title="Reichweite" 
                    value={totalKPIs.reach} 
                    icon={Users}
                    color="blue-500"
                />
                <KPICard 
                    title="Impressionen" 
                    value={totalKPIs.impressions} 
                    icon={Eye}
                    color="purple-500"
                />
                <KPICard 
                    title="Klicks" 
                    value={totalKPIs.clicks} 
                    icon={MousePointer}
                    color="green-500"
                />
                <KPICard 
                    title="Engagement" 
                    value={`${(totalKPIs.engagement / Math.max(filteredReports.length, 1)).toFixed(2)}%`} 
                    icon={Heart}
                    color="pink-500"
                />
            </div>

            {/* Charts */}
            {chartData.length > 0 && (
                <div className="grid lg:grid-cols-2 gap-6">
                    {/* Bar Chart - Reach by Channel */}
                    <Card className="shadow-soft dark:bg-gray-800/50 dark:border-gray-700">
                        <CardHeader>
                            <CardTitle className="text-lg">Reichweite nach Kanal</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                                    <XAxis dataKey="name" tick={{ fill: '#9CA3AF' }} />
                                    <YAxis tick={{ fill: '#9CA3AF' }} />
                                    <Tooltip 
                                        contentStyle={{ 
                                            backgroundColor: '#1F2937', 
                                            border: 'none', 
                                            borderRadius: '8px',
                                            color: '#fff'
                                        }}
                                        formatter={(value) => [value.toLocaleString('de-DE'), 'Reichweite']}
                                    />
                                    <Bar dataKey="reach" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </CardContent>
                    </Card>

                    {/* Line Chart - Engagement */}
                    <Card className="shadow-soft dark:bg-gray-800/50 dark:border-gray-700">
                        <CardHeader>
                            <CardTitle className="text-lg">Engagement Rate</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <ResponsiveContainer width="100%" height={300}>
                                <AreaChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                                    <XAxis dataKey="name" tick={{ fill: '#9CA3AF' }} />
                                    <YAxis tick={{ fill: '#9CA3AF' }} />
                                    <Tooltip 
                                        contentStyle={{ 
                                            backgroundColor: '#1F2937', 
                                            border: 'none', 
                                            borderRadius: '8px',
                                            color: '#fff'
                                        }}
                                        formatter={(value) => [`${value}%`, 'Engagement']}
                                    />
                                    <Area 
                                        type="monotone" 
                                        dataKey="engagement" 
                                        stroke="#8B5CF6" 
                                        fill="#8B5CF6" 
                                        fillOpacity={0.3}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Detailed Stats Grid */}
            <Card className="shadow-soft dark:bg-gray-800/50 dark:border-gray-700">
                <CardHeader>
                    <CardTitle className="text-lg">Detaillierte Statistiken</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <div className="p-4 rounded-lg bg-muted/30 dark:bg-gray-700/30 text-center">
                            <Heart className="w-6 h-6 text-red-500 mx-auto mb-2" />
                            <p className="text-2xl font-bold dark:text-white">{totalKPIs.likes.toLocaleString('de-DE')}</p>
                            <p className="text-sm text-muted-foreground">Likes</p>
                        </div>
                        <div className="p-4 rounded-lg bg-muted/30 dark:bg-gray-700/30 text-center">
                            <MessageCircle className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                            <p className="text-2xl font-bold dark:text-white">{totalKPIs.comments.toLocaleString('de-DE')}</p>
                            <p className="text-sm text-muted-foreground">Kommentare</p>
                        </div>
                        <div className="p-4 rounded-lg bg-muted/30 dark:bg-gray-700/30 text-center">
                            <Share2 className="w-6 h-6 text-green-500 mx-auto mb-2" />
                            <p className="text-2xl font-bold dark:text-white">{totalKPIs.shares.toLocaleString('de-DE')}</p>
                            <p className="text-sm text-muted-foreground">Shares</p>
                        </div>
                        <div className="p-4 rounded-lg bg-muted/30 dark:bg-gray-700/30 text-center">
                            <Users className="w-6 h-6 text-purple-500 mx-auto mb-2" />
                            <p className="text-2xl font-bold dark:text-white">+{totalKPIs.followers_gained.toLocaleString('de-DE')}</p>
                            <p className="text-sm text-muted-foreground">Neue Follower</p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default ReportsSection;
